import React, { useMemo, useState } from 'react';
import WorldMap from '../../components/WorldMap';
import ContextMenu from '../../components/ContextMenu';
import { type LiveVehicleTrack } from '../../components/WorldMap';
import {
  getInterventionDisplayLines,
  getInterventionPhaseUiKey,
  effectiveMoyensRequired,
  type InstanceRow,
} from '../../components/interventions/instanceModel';
import { Batiment, Vehicule } from '../../../../shared/types/game';
import { type ContextMenuState } from '../hooks/useMapData';
import RadioJournalDispatch from '../../components/interventions/RadioJournalDispatch';
import UnitsMonitorTable from './UnitsMonitorTable';

interface DispatchViewProps {
  activeInterventions: InstanceRow[];
  selectedIntervention: InstanceRow | null;
  selectedDisplay: { headline: string; subline: string } | null;
  setSelectedInterventionId: (id: number) => void;
  getInterventionPriority: (inv: InstanceRow) => number;
  setInterventionPriority: (inv: InstanceRow, priority: number) => void;
  setMainView: (view: 'dispatch' | 'resources' | 'buildings' | 'interventions') => void;
  buildings: Batiment[];
  interventions: InstanceRow[];
  liveVehicleTracks: LiveVehicleTrack[];
  showInterventionSectors: boolean;
  contextMenu: ContextMenuState;
  setContextMenu: (state: ContextMenuState) => void;
  handleBuildingPlace: (lat: number, lng: number, buildingType: string, buildingName: string) => void;
  availableVehicles: number;
  availablePersonnel: number;
  availabilityPercent: number;
  availabilityCircumference: number;
  availabilityOffset: number;
  handleFutureFeature: (label: string) => void;
  /** Parc du joueur — alimente le moniteur d'engins sous la carte. */
  playerVehicles: Vehicule[];
  selectedInterventionVehicleRows: Vehicule[];
}

const DispatchView: React.FC<DispatchViewProps> = ({
  activeInterventions,
  selectedIntervention,
  selectedDisplay,
  setSelectedInterventionId,
  getInterventionPriority,
  setInterventionPriority,
  setMainView,
  buildings,
  interventions,
  liveVehicleTracks,
  showInterventionSectors,
  contextMenu,
  setContextMenu,
  handleBuildingPlace,
  availableVehicles,
  availablePersonnel,
  availabilityPercent,
  availabilityCircumference,
  availabilityOffset,
  handleFutureFeature,
  playerVehicles,
  selectedInterventionVehicleRows: _selectedInterventionVehicleRows,
}) => {
  // Carte tactique en mode nuit par défaut (habillage proto).
  const [mapNight, setMapNight] = useState(true);
  // File opérationnelle : onglet actif (habillage proto).
  const [queueTab, setQueueTab] = useState<'wait' | 'progress' | 'all'>('all');

  const queue = useMemo(() => {
    const wait = activeInterventions.filter((i) => getInterventionPhaseUiKey(i) === 'wait');
    const progress = activeInterventions.filter((i) => ['travel', 'site'].includes(getInterventionPhaseUiKey(i)));
    return { wait, progress };
  }, [activeInterventions]);

  const shownQueue =
    queueTab === 'wait' ? queue.wait : queueTab === 'progress' ? queue.progress : activeInterventions;

  const elapsedLabel = (inv: InstanceRow): string => {
    const start = inv.date_creation ? new Date(inv.date_creation).getTime() : NaN;
    if (!Number.isFinite(start)) return '';
    const s = Math.max(0, Math.floor((Date.now() - start) / 1000));
    return `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;
  };

  return (
  <div className="grid h-full grid-cols-[360px,minmax(0,1fr),380px] gap-3">
    <aside className="flex min-h-0 flex-col gap-3">
      <section
        className="min-h-0 flex-1 flex flex-col overflow-hidden rounded-lg shadow-xl shadow-black/25"
        style={{ border: '1px solid #223046', background: 'linear-gradient(180deg,rgba(17,25,40,.96),rgba(11,17,28,.96))' }}
      >
        <div
          className="shrink-0 flex items-center justify-between gap-2 px-2.5 py-1.5"
          style={{ borderBottom: '1px solid #223046', background: 'linear-gradient(180deg,#141f31,#0e1725)' }}
        >
          <h2 style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.1em', color: '#dce5f2' }} className="uppercase">
            File opérationnelle
          </h2>
          <div className="flex items-center gap-1">
            {([
              ['wait', 'Attente', queue.wait.length],
              ['progress', 'En cours', queue.progress.length],
              ['all', 'Toutes', activeInterventions.length],
            ] as const).map(([key, label, n]) => (
              <button
                key={key}
                type="button"
                onClick={() => setQueueTab(key)}
                style={{
                  fontSize: 9.5,
                  fontWeight: 700,
                  padding: '2px 7px',
                  borderRadius: 4,
                  border: `1px solid ${queueTab === key ? '#134e3a' : '#223046'}`,
                  background: queueTab === key ? 'rgba(16,185,129,.14)' : 'transparent',
                  color: queueTab === key ? '#6ee7b7' : '#9fb0c6',
                }}
              >
                {label} {n}
              </button>
            ))}
          </div>
        </div>
        <div className="flex-1 min-h-0 space-y-1.5 overflow-y-auto p-2">
          {shownQueue.map((inv) => {
            const lines = getInterventionDisplayLines(inv, 'Intervention');
            const priority = getInterventionPriority(inv);
            const phase = getInterventionPhaseUiKey(inv);
            const accent = phase === 'wait' ? '#ef4444' : phase === 'travel' ? '#f59e0b' : phase === 'site' ? '#10b981' : '#6b7d95';
            const statusLabel = phase === 'wait' ? 'ATTENTE' : phase === 'travel' ? 'TRANSIT' : phase === 'site' ? 'SUR SITE' : 'EN COURS';
            const need = effectiveMoyensRequired(inv);
            const engaged = Array.isArray((inv as unknown as { dispatch_player_vehicle_ids?: unknown[] }).dispatch_player_vehicle_ids)
              ? (inv as unknown as { dispatch_player_vehicle_ids: unknown[] }).dispatch_player_vehicle_ids.length
              : 0;
            const selected = selectedIntervention?.id === inv.id;
            return (
              <button
                key={inv.id}
                type="button"
                onClick={() => setSelectedInterventionId(inv.id)}
                className="w-full text-left"
                style={{
                  display: 'block',
                  borderRadius: 5,
                  border: `1px solid ${selected ? accent : '#223046'}`,
                  borderLeft: `3px solid ${accent}`,
                  background: selected ? 'rgba(56,189,248,.06)' : '#0e1725',
                  padding: '6px 8px',
                }}
              >
                <div className="flex items-center justify-between gap-2">
                  <span style={{ fontFamily: 'monospace', fontSize: 9.5, color: '#6b7d95' }}>
                    {inv.numero_intervention || inv.id}
                  </span>
                  <span
                    style={{
                      fontSize: 9,
                      fontWeight: 800,
                      color: '#fff',
                      background: accent,
                      borderRadius: 3,
                      padding: '0 5px',
                    }}
                  >
                    P{priority}
                  </span>
                </div>
                <p className="mt-0.5 truncate" style={{ fontSize: 12, fontWeight: 700, color: '#dce5f2' }}>
                  {lines.headline}
                </p>
                <p className="truncate" style={{ fontSize: 10, color: '#9fb0c6' }}>
                  {lines.subline || 'Adresse non précisée'}
                </p>
                <div className="mt-1 flex items-center justify-between" style={{ fontSize: 9.5 }}>
                  <span style={{ display: 'inline-flex', gap: 5, alignItems: 'center' }}>
                    <span style={{ color: accent, fontWeight: 700 }}>{statusLabel}</span>
                    <span style={{ fontFamily: 'monospace', color: '#9fb0c6' }}>{elapsedLabel(inv)}</span>
                  </span>
                  <span style={{ fontFamily: 'monospace', color: engaged >= need && need > 0 ? '#6ee7b7' : '#9fb0c6' }}>
                    {engaged}/{need || 1}
                  </span>
                </div>
              </button>
            );
          })}
          {shownQueue.length === 0 ? (
            <p style={{ color: '#6b7d95', fontSize: 11 }} className="px-2 py-6 text-center">
              {queueTab === 'wait' ? 'Aucune intervention en attente' : queueTab === 'progress' ? 'Aucune intervention en cours' : 'Aucune intervention active'}
            </p>
          ) : null}
        </div>
      </section>

      <section className="shrink-0 rounded-lg border border-slate-700/75 bg-slate-900/90 shadow-xl shadow-black/25">
        <div className="border-b border-slate-700/70 px-3 py-2">
          <h3 className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-200">
            {selectedDisplay?.headline || 'Sélectionner une intervention'}
          </h3>
        </div>
        {selectedIntervention ? (
          <div className="grid grid-cols-2 gap-x-3 gap-y-1 px-3 py-2 text-[11px]">
            <div>
              <p className="text-slate-500 text-[10px] uppercase tracking-wide">Adresse</p>
              <p className="text-slate-100 truncate">{selectedDisplay?.subline || '—'}</p>
            </div>
            <div>
              <p className="text-slate-500 text-[10px] uppercase tracking-wide">Appel reçu</p>
              <p className="text-slate-100">
                {selectedIntervention.date_creation
                  ? new Date(selectedIntervention.date_creation).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })
                  : '—'}
              </p>
            </div>
            <div className="col-span-2 mt-1">
              <button
                type="button"
                onClick={() => setMainView('interventions')}
                className="w-full rounded border border-sky-500/45 bg-sky-500/10 py-1.5 text-[10px] font-semibold uppercase tracking-wide text-sky-200 hover:bg-sky-500/20"
              >
                Gérer cette intervention →
              </button>
            </div>
          </div>
        ) : (
          <p className="px-3 py-2 text-[11px] text-slate-500">Cliquez sur une intervention pour voir ses détails.</p>
        )}
      </section>
    </aside>

    <main className="grid min-h-0 grid-rows-[minmax(0,1fr),340px] gap-3">
      <section
        className={`relative min-h-0 overflow-hidden rounded-lg shadow-xl shadow-black/30 ${mapNight ? 'map-night' : ''}`}
        style={{ border: '1px solid #223046', background: '#0a121e' }}
      >
        <WorldMap
          buildings={buildings}
          showInterventionSectors={showInterventionSectors}
          interventions={interventions}
          liveVehicles={liveVehicleTracks}
        />

        {/* Barre d'outils carte (habillage proto : « Carte tactique » + bascules). */}
        <div
          className="pointer-events-none absolute inset-x-0 top-0 z-[500] flex items-center justify-between px-2 py-1.5"
          style={{ background: 'linear-gradient(180deg,rgba(10,18,30,.92),transparent)' }}
        >
          <span
            style={{ fontSize: 10, fontWeight: 700, letterSpacing: '.12em', color: '#9fb0c6' }}
            className="uppercase"
          >
            Carte tactique
          </span>
          <div className="pointer-events-auto flex items-center gap-1">
            <button
              type="button"
              onClick={() => setMapNight((v) => !v)}
              style={{
                fontSize: 9,
                fontWeight: 700,
                letterSpacing: '.05em',
                padding: '2px 7px',
                borderRadius: 4,
                border: `1px solid ${mapNight ? '#134e3a' : '#223046'}`,
                background: mapNight ? 'rgba(16,185,129,.14)' : '#172234',
                color: mapNight ? '#6ee7b7' : '#9fb0c6',
              }}
              className="uppercase"
            >
              Nuit
            </button>
          </div>
        </div>

        {contextMenu && (
          <ContextMenu
            x={contextMenu.x}
            y={contextMenu.y}
            onClose={() => setContextMenu(null)}
            onBuildClick={(buildingType, buildingName) =>
              handleBuildingPlace(contextMenu.lat, contextMenu.lng, buildingType, buildingName)
            }
          />
        )}
      </section>

      {/* Habillage proto : sous la carte, le moniteur d'engins (pièce maîtresse du dispatch). */}
      <section className="min-h-0 flex-1">
        <UnitsMonitorTable
          playerVehicles={playerVehicles}
          buildings={buildings}
          interventions={interventions}
          liveVehicleTracks={liveVehicleTracks}
        />
      </section>
    </main>

    {/* Colonne 3 : panneau contextuel (comme le proto). L'armement n'y est plus —
        il est passé dans la colonne Équipage du moniteur d'engins. */}
    <aside className="flex min-h-0 flex-col gap-3">
      <section className="min-h-0 flex-1 rounded-lg border border-slate-700/75 bg-slate-900/90 shadow-xl shadow-black/25 flex flex-col">
        <div className="shrink-0 border-b border-slate-700/70 px-3 py-2 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            {/* Icône radio */}
            <svg className="w-3.5 h-3.5 text-amber-400 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.111 16.404a5.5 5.5 0 017.778 0M12 20h.01m-7.08-7.071c3.904-3.905 10.236-3.905 14.141 0M1.394 9.393c5.857-5.857 15.355-5.857 21.213 0" />
            </svg>
            <h3 className="text-[11px] font-semibold uppercase tracking-[0.12em] text-amber-300/90">
              Journal radio
            </h3>
          </div>
          {selectedIntervention && (
            <span className="text-[10px] text-slate-500 font-mono truncate max-w-[120px]">
              #{selectedIntervention.numero_intervention || selectedIntervention.id}
            </span>
          )}
        </div>
        <div className="flex-1 min-h-0 overflow-y-auto">
          {selectedIntervention ? (
            <RadioJournalDispatch instanceId={selectedIntervention.id} />
          ) : (
            <div className="flex flex-col items-center justify-center h-full py-8 px-4 text-center">
              <svg className="w-8 h-8 text-slate-700 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8.111 16.404a5.5 5.5 0 017.778 0M12 20h.01m-7.08-7.071c3.904-3.905 10.236-3.905 14.141 0M1.394 9.393c5.857-5.857 15.355-5.857 21.213 0" />
              </svg>
              <p className="text-[11px] text-slate-600">Sélectionnez une intervention<br/>pour voir son journal radio</p>
            </div>
          )}
        </div>
      </section>
    </aside>
  </div>
  );
};

export default DispatchView;
